import { partStarts, voiceTime, type Replay, type VoiceTrack } from "@69ly/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { voiceAudioUrl } from "../lib/demo-queries.ts";
import { VoiceEngine, type TrackStats } from "./voice-engine.ts";

/** Above this speed the voice is not played: it could not be understood. */
export const VOICE_MAX_SPEED = 2;

/** How each track is heard, kept per map in this browser. */
export interface VoiceMix {
  /** Voice on or off for the whole map. */
  on: boolean;
  volume: Record<string, number>;
  muted: Record<string, boolean>;
  /** The one track heard alone, if any. */
  solo: string | null;
}

const EMPTY_MIX: VoiceMix = { on: true, volume: {}, muted: {}, solo: null };
const mixKey = (demoId: string) => `69ly.voiceMix.${demoId}`;

export function useVoiceMix(demoId: string): [VoiceMix, (change: Partial<VoiceMix> | ((m: VoiceMix) => Partial<VoiceMix>)) => void] {
  const [mix, setMix] = useState<VoiceMix>(() => {
    try {
      return { ...EMPTY_MIX, ...(JSON.parse(localStorage.getItem(mixKey(demoId)) ?? "{}") as Partial<VoiceMix>) };
    } catch {
      return EMPTY_MIX;
    }
  });
  const update = useCallback(
    (change: Partial<VoiceMix> | ((m: VoiceMix) => Partial<VoiceMix>)) =>
      setMix((m) => {
        const next = { ...m, ...(typeof change === "function" ? change(m) : change) };
        try {
          localStorage.setItem(mixKey(demoId), JSON.stringify(next));
        } catch {
          // Private windows: the mix lasts for the visit.
        }
        return next;
      }),
    [demoId],
  );
  return [mix, update];
}

/** Whether a track is heard with this mix. */
export const audible = (mix: VoiceMix, id: string) => mix.on && !mix.muted[id] && (mix.solo === null || mix.solo === id);

/** "12:34.5", "1:02:03.5"; "−0:03.0" before the recording starts. */
export function audioClock(seconds: number): string {
  const sign = seconds < 0 ? "−" : "";
  const s = Math.abs(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = (s % 60).toFixed(1).padStart(4, "0");
  return h ? `${sign}${h}:${String(m).padStart(2, "0")}:${rest}` : `${sign}${m}:${rest}`;
}

/** "12:34", "12:34.5", "1:02:03", "754.2" → seconds; null when it is not a time. */
export function parseAudioClock(text: string): number | null {
  const parts = text.trim().replace(",", ".").split(":");
  if (parts.length > 3 || parts.some((p) => !/^\d+(\.\d+)?$/.test(p))) return null;
  const n = parts.map(Number);
  if (n.slice(1).some((x) => x >= 60)) return null;
  return n.reduce((total, x) => total * 60 + x, 0);
}

export interface VoicePlayback {
  /** Each track's length in seconds, once its file has said. */
  durations: Record<string, number>;
  /** Where each track is at this tick (it may be before its start or past its end). */
  at: (track: VoiceTrack) => number;
  /** How each track is played and how often it was put back in step (`?debug=voice`). */
  stats: () => TrackStats[];
}

/**
 * Plays the voice tracks along the replay: each one at the second of its audio
 * that goes with the tick on screen, at the replay's speed. The engine
 * (voice-engine.ts) plays them with Web Audio where it can, and puts a track
 * back in place only when the replay jumps.
 */
export function useVoicePlayback({
  demoId,
  tracks,
  mix,
  replay,
  tick,
  playing,
  speed,
}: {
  demoId: string;
  tracks: VoiceTrack[];
  mix: VoiceMix;
  replay: Replay;
  tick: number;
  playing: boolean;
  speed: number;
}): VoicePlayback {
  const [durations, setDurations] = useState<Record<string, number>>({});
  const engine = useRef<VoiceEngine | null>(null);
  const starts = partStarts(replay);

  useEffect(() => {
    const made = new VoiceEngine((id, seconds) => setDurations((d) => (d[id] === seconds ? d : { ...d, [id]: seconds })));
    engine.current = made;
    return () => {
      made.dispose();
      if (engine.current === made) engine.current = null;
    };
  }, []);

  useEffect(() => {
    engine.current?.setTracks(tracks.map((t) => ({ id: t.id, url: voiceAudioUrl(demoId, t.id), contentType: t.contentType, bytes: t.bytes })));
  }, [demoId, tracks]);

  const at = useCallback((track: VoiceTrack) => voiceTime(track.offsets, starts, replay.tickRate, tick), [starts, replay.tickRate, tick]);

  useEffect(() => {
    const e = engine.current;
    if (!e) return;
    for (const track of tracks) {
      e.sync(track.id, {
        target: at(track),
        play: playing && speed <= VOICE_MAX_SPEED && audible(mix, track.id),
        speed,
        volume: mix.volume[track.id] ?? 1,
      });
    }
  }, [tracks, mix, at, playing, speed]);

  const stats = useCallback(() => engine.current?.stats() ?? [], []);
  return { durations, at, stats };
}
